import { createSlice } from '@reduxjs/toolkit'
import {
  getEncounter,
  performAiCodeCheck,
  refreshEncounter,
  saveEncounterField,
  updateMDM,
  updatePanelMips,
} from './sidePanelThunk'
import { toast } from 'react-toastify'
import { VISIT_STATUS } from '../../utils/statusConstants'

const initialState = {
  aiCodeCheckFiring: false,
  autoSaveDraft: false,
  changesMade: false,
  dxAutoSelected: false,
  isLastEncounter: false,
  isReadOnly: true,
  isSaving: false,
  panelChargeId: null,
  panelChart: null,
  panelChartHelpers: {},
  panelEncounter: null,
  panelEncHistory: [],
  panelMipsError: null,
  panelMipsMeasures: [],
  panelPatientPCCData: {},
  startSideTabIndex: 0,
  startTopTabIndex: 0,
}
const sidePanelSlice = createSlice({
  name: 'sidePanel',
  initialState,
  reducers: {
    clearSidePanel: (state) => {
      if (!state.isSaving) {
        if (state.changesMade) {
          toast.success('Updates saved successfully.')
        }
        Object.keys(initialState).forEach((key) => {
          state[key] = initialState[key]
        })
      }
    },
    setAiCodeCheckStatus: (state, action) => {
      state.aiCodeCheckFiring = action.payload
    },
    setAutoSaveDraft: (state, action) => {
      state.autoSaveDraft = action.payload
    },
    setDxAutoSelected: (state) => {
      state.dxAutoSelected = true
    },
    setMipsMeasures: (state, action) => {
      const { mips, useLastAnswers } = action.payload
      const prevMips = [...state.panelMipsMeasures]

      const newMips = (mips || []).map((newMip) => {
        if (!useLastAnswers) {
          return newMip
        }

        let oldMip = prevMips.find((old) => old._id === newMip._id)
        if (oldMip && oldMip.value) {
          return { ...newMip, value: oldMip.value }
        }
        return newMip
      })
      state.panelMipsMeasures = newMips
    },
    setTabIndexes: (state, action) => {
      state.startSideTabIndex = action.payload?.side || 0
      state.startTopTabIndex = action.payload?.top || 0
    },
    setPanelDetails: (state, action) => {
      state.panelChargeId = action.payload?.visitId
      state.isLastEncounter = Boolean(action.payload?.isLastEncounter)
    },
    setPanelChartHelper: (state, action) => {
      if (action.payload?.field) {
        state.panelChartHelpers[action.payload.field] = action.payload?.value
      }
    },
    setPanelPCCHelper: (state, action) => {
      if (action.payload?.field) {
        state.panelPatientPCCData[action.payload.field] = action.payload?.value
      }
    },
    // set encounter value
    setEncounterField: (state, action) => {
      if (!state.panelEncounter) {
        return
      }

      const field = action.payload?.field
      const value = action.payload?.value
      const grouping = action.payload?.grouping
      if (!field) {
        return
      }
      if (grouping) {
        if (!state.panelEncounter[grouping] && grouping === 'code_assist') {
          state.panelEncounter[grouping] = {
            diagnosis_notes: '',
            billing_notes: '',
            audio_notes: [],
            templates: [],
          }
        }
        state.panelEncounter[grouping][field] = value
      } else {
        state.panelEncounter[field] = value
      }

      if (!action.payload?.skipSaveLock) {
        state.isSaving = true
        state.changesMade = true
      }
    },
    setAiDxCodes: (state, action) => {
      if (!state.panelEncounter) {
        return
      }

      state.panelEncounter.aiDxCodes = action.payload
      state.changesMade = true
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getEncounter.fulfilled, (state, action) => {
        if (action.payload) {
          state.panelEncounter = action.payload.currentVisit
          state.panelEncHistory = action.payload.visitHistory
          state.panelChart = action.payload.currentChart

          if (state.isLastEncounter) {
            resetPerEncounterFields(state)
          }
        }

        calculateReadOnly(state)
        if (!action.payload?.currentVisit) {
          sidePanelSlice.caseReducers.clearSidePanel(state)
        }
      })
      .addCase(getEncounter.rejected, (state, action) => {
        if (action.payload === 'ACCESS_ERROR') {
          sidePanelSlice.caseReducers.clearSidePanel(state)
        }
      })
      .addCase(saveEncounterField.fulfilled, (state, action) => {
        sidePanelSlice.caseReducers.setEncounterField(state, action)

        if (action.payload?.cloneUpdates) {
          action.payload.cloneUpdates.forEach((update) => {
            sidePanelSlice.caseReducers.setEncounterField(state, update)
          })

          state.isLastEncounter = false
        }

        state.isSaving = false
        state.changesMade = true
      })
      .addCase(saveEncounterField.pending, (state, action) => {
        state.isSaving = true
      })
      .addCase(saveEncounterField.rejected, (state, action) => {
        state.isSaving = false
        if (action?.payload?.type === 'clone') {
          state.isLastEncounter = true
        }
      })
      .addCase(refreshEncounter.fulfilled, (state, action) => {
        if (action.payload?.refreshedEncounter) {
          state.panelEncounter = {
            ...state.panelEncounter,
            admitPatientEvent: undefined,
            dischargePatientEvent: undefined,
            coding_ai: undefined,
            ...action.payload.refreshedEncounter,
          }
          state.panelChart = !state.isLastEncounter ? action.payload.refreshedChart : undefined

          if (!action.payload.maintainLastEncounter) {
            state.isLastEncounter = false
          }

          if (action.payload.resetClonedFields) {
            resetPerEncounterFields(state)
          }

          if (!action.payload?.useAiCpt) {
            // this has devolved into an "everything we want to make sure gets removed when the encounter panel reloads" list
            state.panelEncounter.aiCptCodeCheck = undefined
            state.panelEncounter.aiDxCodes = undefined
          }

          calculateReadOnly(state)
        }
      })
      .addCase(updateMDM.fulfilled, (state, action) => {
        if (state.panelEncounter?.coding_ai?.anthropic && action.payload) {
          state.panelEncounter.coding_ai.anthropic.mdmelements = {
            ...(state.panelEncounter.coding_ai.anthropic?.mdmelements || {}),
            ...action.payload,
          }
        }
      })
      .addCase(updatePanelMips.fulfilled, (state, action) => {
        state.panelMipsError = action.payload
      })
      .addCase(performAiCodeCheck.fulfilled, (state, action) => {
        // THIS CANNOT REPLACE DX AND CPT, BECAUSE REFRESH IS CALLED AFTER WE CALL THIS
        if (state.panelEncounter) {
          state.dxAutoSelected = false

          // cpt
          let newAiCPT = action.payload?.ICD10PCS
          if (newAiCPT) {
            state.panelEncounter.aiCptCodeCheck = {
              ...newAiCPT,
              modifiers: [],
              units: 1,
              rvu: null,
              places_of_service: null,
            }
          }

          //dx
          let newDx = action.payload?.ICD10CM || []
          // dont add dupe DX codes
          newDx = newDx.filter(
            (dx) =>
              !(state.panelEncounter.aiDxCodes || [])?.find((oldDx) => oldDx.code === dx.code),
          )
          // only keep the data we want
          newDx = newDx.map((dx) => {
            return {
              code: dx.code,
              description: dx.description,
              enabled: dx.enabled,
              notValidAsMainDx: dx.notValidAsMainDx,
              nta_points: dx.nta_points,
              pdpm_clinical_category: dx.pdpm_clinical_category,
              pdpm_return: dx.pdpm_return,
              pdpm_snf_comment: dx.pdpm_snf_comment,
              type: dx.type,
            }
          })

          // This may be redundant, because there is no aiDxCodes in the DB, so when the panel refreshes, this seems to refresh too
          state.panelEncounter.aiDxCodes = [...(state.panelEncounter.aiDxCodes || []), ...newDx]
          state.changesMade = true
        }
      })
  },
})

function resetPerEncounterFields(state) {
  // dont let neurosuite ever carry over
  state.panelEncounter.neuro_suite = false
  // dont let pending_cosign ever carry over
  state.panelEncounter.pending_cosign = false
  // dont carry over SSP
  state.panelEncounter.incident_to_provider_id = null
  state.panelEncounter.incident_to_provider_substantive = false
  // do not carry over modifiers
  state.panelEncounter.procedureCodes = state.panelEncounter.procedureCodes[0]
    ? [{ ...state.panelEncounter.procedureCodes[0], modifiers: [], units: 1 }]
    : []
}

function calculateReadOnly(state) {
  // metadata to help us know how to treat the encounter
  if (!state.isLastEncounter) {
    switch (state.panelEncounter?.status) {
      case 'Bill audit':
      case 'Bill hold':
      case VISIT_STATUS.bill_submitted:
      case 'Claim accepted':
      case VISIT_STATUS.claim_assigned:
      case 'Claim denied':
      case 'Claim hold':
      case 'Claim processed':
      case 'Claim received':
      case 'Claim rejected':
      case VISIT_STATUS.completed:
      case 'Patient assigned':
      case 'Patient hold':
      case 'Patient payment':
      case 'Patient statement sent':
      case 'Settled':
        state.isReadOnly = true
        return
      default:
        state.isReadOnly = false
        return
    }
  } else {
    state.isReadOnly = false
  }
}

export const {
  setPanelChartHelper,
  setPanelDetails,
  clearSidePanel,
  setTabIndexes,
  setAiDxCodes,
  setAutoSaveDraft,
  setEncounterField,
  setMipsMeasures,
  setAiCodeCheckStatus,
  setPanelPCCHelper,
  setDxAutoSelected,
} = sidePanelSlice.actions
export default sidePanelSlice.reducer
